import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AdvancedFilters = ({ onFiltersChange, onSaveView, savedViews = [] }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: { start: '', end: '' },
    departments: [],
    employees: [],
    shiftTypes: [],
    status: [],
    priority: '',
    skills: [],
    availability: ''
  });
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  const departmentOptions = [
    { value: 'customer-service', label: 'Customer Service' },
    { value: 'operations', label: 'Operations' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'administration', label: 'Administration' },
    { value: 'maintenance', label: 'Maintenance' }
  ];

  const employeeOptions = [
    { value: 'emp001', label: 'Anna Müller' },
    { value: 'emp002', label: 'Thomas Weber' },
    { value: 'emp003', label: 'Maria Schmidt' },
    { value: 'emp004', label: 'Klaus Fischer' },
    { value: 'emp005', label: 'Sarah Bauer' },
    { value: 'emp006', label: 'Michael König' },
    { value: 'emp007', label: 'Lisa Wagner' },
    { value: 'emp008', label: 'Peter Hoffmann' }
  ];

  const shiftTypeOptions = [
    { value: 'morning', label: 'Frühschicht (06:00-14:00)' },
    { value: 'afternoon', label: 'Spätschicht (14:00-22:00)' },
    { value: 'night', label: 'Nachtschicht (22:00-06:00)' },
    { value: 'split', label: 'Geteilte Schicht' },
    { value: 'overtime', label: 'Überstunden' }
  ];

  const statusOptions = [
    { value: 'assigned', label: 'Zugewiesen' },
    { value: 'pending', label: 'Ausstehend' },
    { value: 'vacant', label: 'Offen' },
    { value: 'conflict', label: 'Konflikt' },
    { value: 'completed', label: 'Abgeschlossen' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Niedrig' },
    { value: 'medium', label: 'Mittel' },
    { value: 'high', label: 'Hoch' },
    { value: 'urgent', label: 'Dringend' }
  ];

  const skillOptions = [
    { value: 'phone-support', label: 'Telefon-Support' },
    { value: 'chat-support', label: 'Chat-Support' },
    { value: 'email-support', label: 'Email-Support' },
    { value: 'technical', label: 'Technisch' },
    { value: 'logistics', label: 'Logistik' },
    { value: 'quality-control', label: 'Qualitätskontrolle' },
    { value: 'assembly', label: 'Montage' },
    { value: 'maintenance', label: 'Wartung' },
    { value: 'security', label: 'Sicherheit' }
  ];

  const availabilityOptions = [
    { value: 'available', label: 'Verfügbar' },
    { value: 'partially-available', label: 'Teilweise verfügbar' },
    { value: 'unavailable', label: 'Nicht verfügbar' },
    { value: 'on-leave', label: 'Im Urlaub' },
    { value: 'sick', label: 'Krank' }
  ];

  const presetFilters = [
    {
      id: 'today-vacant',
      name: 'Heute - Offene Schichten',
      icon: 'AlertCircle',
      filters: {
        dateRange: { start: new Date()?.toISOString()?.split('T')?.[0], end: new Date()?.toISOString()?.split('T')?.[0] },
        status: ['vacant']
      }
    },
    {
      id: 'week-conflicts',
      name: 'Woche - Konflikte',
      icon: 'AlertTriangle',
      filters: {
        dateRange: { 
          start: new Date()?.toISOString()?.split('T')?.[0], 
          end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0] 
        },
        status: ['conflict']
      }
    },
    {
      id: 'high-priority',
      name: 'Hohe Priorität',
      icon: 'Star',
      filters: {
        priority: 'high'
      }
    },
    {
      id: 'night-shifts',
      name: 'Nachtschichten',
      icon: 'Moon',
      filters: {
        shiftTypes: ['night']
      }
    }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Count active filters
    const count = Object.values(newFilters)?.reduce((acc, val) => {
      if (Array.isArray(val)) return acc + val?.length;
      if (typeof val === 'object' && val !== null) {
        return acc + Object.values(val)?.filter(v => v !== '')?.length;
      }
      return acc + (val !== '' ? 1 : 0);
    }, 0);
    
    setActiveFiltersCount(count);
    onFiltersChange?.(newFilters);
  };

  const handlePresetFilter = (preset) => {
    setFilters(preset?.filters);
    setActiveFiltersCount(Object.keys(preset?.filters)?.length);
    onFiltersChange?.(preset?.filters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      dateRange: { start: '', end: '' },
      departments: [],
      employees: [],
      shiftTypes: [],
      status: [],
      priority: '',
      skills: [],
      availability: ''
    };
    setFilters(clearedFilters);
    setActiveFiltersCount(0);
    onFiltersChange?.(clearedFilters);
  };

  const handleSaveCurrentView = () => {
    const viewName = prompt('Name für die gespeicherte Ansicht:');
    if (viewName) {
      onSaveView?.({
        name: viewName,
        filters: filters,
        timestamp: new Date()
      });
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <Icon name="Filter" size={20} className="text-accent" />
            <h3 className="font-semibold text-foreground">Erweiterte Filter</h3>
          </div>
          {activeFiltersCount > 0 && (
            <div className="px-2 py-1 bg-accent/10 text-accent rounded-md text-sm font-medium">
              {activeFiltersCount} aktiv
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="xs"
            onClick={handleClearFilters}
            disabled={activeFiltersCount === 0}
          >
            Zurücksetzen
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={handleSaveCurrentView}
            disabled={activeFiltersCount === 0}
          >
            Ansicht speichern
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
          </Button>
        </div>
      </div>
      {/* Preset Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {presetFilters?.map((preset) => (
          <Button
            key={preset?.id}
            variant="outline"
            size="xs"
            onClick={() => handlePresetFilter(preset)}
            iconName={preset?.icon}
            iconPosition="left"
            iconSize={14}
          >
            {preset?.name}
          </Button>
        ))}
      </div>
      {/* Expanded Filters */}
      {isExpanded && (
        <div className="space-y-4 pt-4 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Date Range */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Zeitraum</label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  placeholder="Von"
                  value={filters?.dateRange?.start}
                  onChange={(e) => handleFilterChange('dateRange', { ...filters?.dateRange, start: e?.target?.value })}
                />
                <Input
                  type="date"
                  placeholder="Bis"
                  value={filters?.dateRange?.end}
                  onChange={(e) => handleFilterChange('dateRange', { ...filters?.dateRange, end: e?.target?.value })}
                />
              </div>
            </div>

            {/* Departments */}
            <Select
              label="Abteilungen"
              multiple
              searchable
              options={departmentOptions}
              value={filters?.departments}
              onChange={(value) => handleFilterChange('departments', value)}
              placeholder="Abteilungen auswählen..."
            />

            {/* Employees */}
            <Select
              label="Mitarbeiter"
              multiple
              searchable
              options={employeeOptions}
              value={filters?.employees}
              onChange={(value) => handleFilterChange('employees', value)}
              placeholder="Mitarbeiter auswählen..."
            />

            {/* Shift Types */}
            <Select
              label="Schichtarten"
              multiple
              options={shiftTypeOptions}
              value={filters?.shiftTypes}
              onChange={(value) => handleFilterChange('shiftTypes', value)}
              placeholder="Schichtarten auswählen..."
            />

            {/* Status */}
            <Select
              label="Status"
              multiple
              options={statusOptions}
              value={filters?.status}
              onChange={(value) => handleFilterChange('status', value)}
              placeholder="Status auswählen..."
            />

            {/* Priority */}
            <Select
              label="Priorität"
              options={priorityOptions}
              value={filters?.priority}
              onChange={(value) => handleFilterChange('priority', value)}
              placeholder="Priorität auswählen..."
            />

            {/* Skills */}
            <Select
              label="Erforderliche Fähigkeiten"
              multiple
              searchable
              options={skillOptions}
              value={filters?.skills}
              onChange={(value) => handleFilterChange('skills', value)}
              placeholder="Fähigkeiten auswählen..."
            />

            {/* Availability */}
            <Select
              label="Verfügbarkeitsstatus"
              options={availabilityOptions}
              value={filters?.availability}
              onChange={(value) => handleFilterChange('availability', value)}
              placeholder="Verfügbarkeit auswählen..."
            />
          </div>

          {/* Saved Views */}
          {savedViews?.length > 0 && (
            <div className="pt-4 border-t border-border">
              <h4 className="text-sm font-medium text-foreground mb-2">Gespeicherte Ansichten</h4>
              <div className="flex flex-wrap gap-2">
                {savedViews?.map((view, index) => (
                  <Button
                    key={index}
                    variant="ghost"
                    size="xs"
                    onClick={() => {
                      setFilters(view?.filters);
                      onFiltersChange?.(view?.filters);
                    }}
                    iconName="Bookmark"
                    iconPosition="left"
                    iconSize={12}
                  >
                    {view?.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedFilters;